import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useSimpleAuth } from '../../contexts/SimpleAuthContext';
import './AuthForms.css';

interface PasswordStrength {
  score: number;
  requirements: {
    minLength: boolean;
    hasUppercase: boolean;
    hasLowercase: boolean;
    hasNumber: boolean;
    hasSpecial: boolean;
  };
}

const checkPasswordStrength = (password: string): PasswordStrength => {
  const requirements = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };

  const metRequirements = Object.values(requirements).filter(Boolean).length;
  let score = 0;
  if (metRequirements >= 5) score = 3;
  else if (metRequirements >= 3) score = 2;
  else if (metRequirements >= 2) score = 1;

  return { score, requirements };
};

export const ResetPasswordForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    score: 0,
    requirements: {
      minLength: false,
      hasUppercase: false,
      hasLowercase: false,
      hasNumber: false,
      hasSpecial: false,
    },
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  const { resetPassword, error, clearError } = useSimpleAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!resetToken) {
      navigate('/auth/forgot-password', { 
        state: { message: 'Invalid or missing reset token. Please request a new one.' } 
      });
    }
  }, [resetToken, navigate]);

  useEffect(() => {
    if (password) {
      setPasswordStrength(checkPasswordStrength(password));
    }
  }, [password]);

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!password) {
      errors.password = 'Password is required';
    } else if (passwordStrength.score === 0) {
      errors.password = 'Password is too weak';
    }
    
    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !resetToken) return;
    
    setIsSubmitting(true);
    clearError();
    
    try {
      await resetPassword(resetToken, password);
      setIsSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/auth/login', { 
          state: { message: 'Password reset successful! Please sign in with your new password.' } 
        });
      }, 3000);
    } catch (err) {
      // Error is handled by context
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h2 className="auth-title">Password Reset Successful</h2>
          <div className="auth-success">
            Your password has been reset successfully! Redirecting you to sign in...
          </div>
          <Link to="/auth/login" className="auth-submit-button" style={{ textAlign: 'center', display: 'block' }}>
            Sign In Now
          </Link>
        </div>
      </div>
    );
  }

  const getPasswordStrengthClass = () => {
    switch (passwordStrength.score) {
      case 1: return 'weak';
      case 2: return 'medium';
      case 3: return 'strong';
      default: return '';
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2 className="auth-title">Create New Password</h2>
        <p className="auth-subtitle">
          Enter your new password below. Make sure it's different from your previous password.
        </p>
        
        {error && (
          <div className="auth-error">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={validationErrors.password ? 'error' : ''}
              placeholder="Enter your new password"
              autoComplete="new-password"
              autoFocus
            />
            {validationErrors.password && (
              <span className="field-error">{validationErrors.password}</span>
            )}
            
            {password && (
              <>
                <div className="password-strength">
                  <div className={`password-strength-bar ${getPasswordStrengthClass()}`} />
                </div>
                <div className="password-requirements">
                  <div className={`password-requirement ${passwordStrength.requirements.minLength ? 'met' : 'unmet'}`}>
                    At least 8 characters
                  </div>
                  <div className={`password-requirement ${passwordStrength.requirements.hasUppercase ? 'met' : 'unmet'}`}>
                    One uppercase letter
                  </div>
                  <div className={`password-requirement ${passwordStrength.requirements.hasLowercase ? 'met' : 'unmet'}`}>
                    One lowercase letter
                  </div>
                  <div className={`password-requirement ${passwordStrength.requirements.hasNumber ? 'met' : 'unmet'}`}>
                    One number
                  </div>
                  <div className={`password-requirement ${passwordStrength.requirements.hasSpecial ? 'met' : 'unmet'}`}>
                    One special character
                  </div>
                </div>
              </>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={validationErrors.confirmPassword ? 'error' : ''}
              placeholder="Confirm your new password"
              autoComplete="new-password"
            />
            {validationErrors.confirmPassword && (
              <span className="field-error">{validationErrors.confirmPassword}</span>
            )}
          </div>
          
          <button
            type="submit"
            className="auth-submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>
        
        <div className="auth-footer">
          <p>
            <Link to="/auth/login" className="auth-link">
              Return to Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};